import React from 'react';
import { useEvents, EventSeries } from './api/useEvents';

/**
 * Series monitor for recurring events — the events counterpart to
 * RecurringDealsPanel.
 *
 * Events have no template row: the generator writes every occurrence with the
 * same `series_id` and `is_recurring = true`, so a "series" is an aggregate over
 * its occurrences (?groupBy=series), not a row you can point at. That is why
 * the health signals below are counts rather than a status column.
 *
 * Worst first — a series that has stopped producing is the thing worth seeing.
 */

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function parseDays(raw: string | string[] | null): string[] {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function repeatsText(s: EventSeries): string {
  const days = parseDays(s.recurrence_days);
  if (!days.length) return s.recurrence_pattern || '—';
  const ordered = DAY_ORDER.filter(d => days.includes(d));
  const short = ordered.map(d => d.slice(0, 1).toUpperCase() + d.slice(1, 3));
  return `${s.recurrence_pattern} · ${short.join(', ')}`;
}

function fmtDate(iso: string | null, tz: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      timeZone: tz || undefined,
      month: 'short',
      day: 'numeric',
      weekday: 'short',
    });
  } catch {
    return iso.slice(0, 10);
  }
}

/** A series with nothing upcoming has stopped producing; one or two left is
 *  about to run dry, since the generator only looks 14 days ahead. */
function health(s: EventSeries): { label: string; tone: 'bad' | 'warn' | 'ok' } {
  if (s.occurrences_upcoming === 0) return { label: 'Not producing', tone: 'bad' };
  if (s.occurrences_upcoming <= 2) return { label: 'Running low', tone: 'warn' };
  return { label: 'Healthy', tone: 'ok' };
}

const TONES = {
  bad: 'bg-red-950/50 border-red-800/60 text-red-300',
  warn: 'bg-amber-950/50 border-amber-800/60 text-amber-300',
  ok: 'bg-emerald-950/50 border-emerald-800/60 text-emerald-300',
};

export function EventSeriesPanel({
  apiUrl, search = '', onOpenSeries,
}: {
  apiUrl?: string;
  search?: string;
  onOpenSeries?: (seriesId: string) => void;
}) {
  const { events, loading, error, refetch } = useEvents({
    apiUrl,
    search,
    groupBySeries: true,
    limit: 100,
  });

  // The hook is typed for Event; groupBy=series returns the aggregate shape.
  const series = events as unknown as EventSeries[];
  const notProducing = series.filter(s => s.occurrences_upcoming === 0).length;
  const runningLow = series.filter(
    s => s.occurrences_upcoming > 0 && s.occurrences_upcoming <= 2,
  ).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm text-gray-300">
          <span className="font-semibold text-white">{series.length}</span> event series
        </span>
        {notProducing > 0 && (
          <span className="px-2.5 py-1 rounded-lg border text-xs font-semibold bg-red-950/50 border-red-800/60 text-red-300">
            {notProducing} not producing
          </span>
        )}
        {runningLow > 0 && (
          <span className="px-2.5 py-1 rounded-lg border text-xs font-semibold bg-amber-950/50 border-amber-800/60 text-amber-300">
            {runningLow} running low
          </span>
        )}
        <button
          onClick={refetch}
          className="ml-auto px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:border-gray-600 transition-colors"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-950/40 border border-red-800/50 rounded-xl text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
        <div className="hidden sm:flex items-center gap-4 px-5 py-3 border-b border-gray-800">
          <p className="flex-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">Series</p>
          <p className="w-44 flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider">Repeats</p>
          <p className="w-20 flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Upcoming</p>
          <p className="w-24 flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Next</p>
          <p className="w-28 flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</p>
        </div>

        {loading && series.length === 0 && (
          <p className="px-5 py-14 text-center text-sm text-gray-500">Loading…</p>
        )}

        {!loading && series.length === 0 && !error && (
          <p className="px-5 py-14 text-center text-sm text-gray-500">
            {search ? `No event series match “${search}”.` : 'No recurring event series yet.'}
          </p>
        )}

        {series.map((s, i) => {
          const h = health(s);
          return (
            <button
              key={s.series_id}
              onClick={() => onOpenSeries?.(s.series_id)}
              className={`w-full text-left flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 sm:px-5 py-3 sm:py-4 hover:bg-gray-800/50 transition-colors ${
                i < series.length - 1 ? 'border-b border-gray-800' : ''
              }`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{s.title}</p>
                <p className="text-xs text-gray-500 truncate">
                  {s.business_name}
                  {s.series_end_date && ` · ends ${String(s.series_end_date).slice(0, 10)}`}
                </p>
              </div>
              <p className="w-44 flex-shrink-0 text-xs text-gray-300">{repeatsText(s)}</p>
              <p className="w-20 flex-shrink-0 text-xs text-gray-300 text-right tabular-nums">
                {s.occurrences_upcoming}
                <span className="text-gray-600"> / {s.occurrences_total}</span>
              </p>
              <p className="w-24 flex-shrink-0 text-xs text-gray-300 text-right">
                {fmtDate(s.next_occurrence, s.business_timezone)}
              </p>
              <span
                className={`w-28 flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-semibold ${TONES[h.tone]}`}
              >
                {h.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
